import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { DEFAULT_LOCALE, localeMeta } from '../locales'
import type { SupportedLocale, TextDirection } from '../types'

// The one tiny i18n store (contract 04 §7). Kept separate from module 02's `uiStore` so
// `shared/i18n` never imports `shared/ui`; module 05 bridges direction into the UI store.
//
// Persistence: `locale` ONLY, under a stable key. `loaded` is an in-memory fetch cache and
// must NOT survive a reload (a fresh session must re-merge message bundles). `direction` is
// DERIVED from the locale and is never independently persisted (contract §7/§18).

/** localStorage key for the persisted UI-locale preference. Also read by `detectInitialLocale`
 *  (step 1 of the fallback chain), so it lives here as the single source of truth. */
export const LOCALE_PERSIST_KEY = 'orphex.locale'

export const useLocaleStore = defineStore(
  'locale',
  () => {
    /** Active UI locale — the source of truth. */
    const locale = ref<SupportedLocale>(DEFAULT_LOCALE)
    /** Locales whose message bundles are already merged (avoids re-fetching a chunk). */
    const loaded = ref<SupportedLocale[]>([])

    const direction = computed<TextDirection>(() => localeMeta[locale.value].dir)
    const intlLocale = computed<string>(() => localeMeta[locale.value].intl)
    const meta = computed(() => localeMeta[locale.value])

    /** Commit the active locale. State-only: message loading + `<html lang>` stamping are
     *  orchestrated by `setLocale` (contract §8) so this stays a pure mutation. */
    function apply(next: SupportedLocale): void {
      locale.value = next
    }

    /** Record that a locale's bundles are merged, once. */
    function markLoaded(next: SupportedLocale): void {
      if (!loaded.value.includes(next)) loaded.value.push(next)
    }

    return { locale, loaded, direction, intlLocale, meta, apply, markLoaded }
  },
  { persist: { key: LOCALE_PERSIST_KEY, pick: ['locale'] } },
)
